import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import DbChatAvatar from '../DbChatAvatar';
import DbChatBubble from '../DbChatBubble';
import DbChatMeta from '../DbChatMeta';
import { styles } from './styles';
import type { DbChatMessageProps } from './types';

const DbChatMessage: React.FC<DbChatMessageProps> = ({
    message,
    showAvatar = true,
    showMyAvatar = true,
    showTime = true,
    showStatus = true,
    showSenderName = false,
    onRetry,
    onLongPress,
    onAvatarPress,
    style,
}) => {
    const { id, type, direction, senderType, content, senderName, senderAvatar, timestamp, status, isRead, isStreaming, isLoading } = message;

    // 系统消息：居中，无头像
    if (type === 'system') {
        return (
            <View style={[styles.systemRow, style]}>
                <DbChatBubble
                    type="system"
                    direction="left"
                    senderType="system"
                    content={content}
                />
            </View>
        );
    }

    const isRight = direction === 'right';
    // 是否实际展示头像：左侧消息用 showAvatar 控制，右侧消息用 showMyAvatar 控制
    const shouldShowAvatar = isRight ? showMyAvatar : showAvatar;

    /** 头像 */
    const avatarNode = shouldShowAvatar ? (
        <DbChatAvatar
            source={senderAvatar}
            senderType={senderType}
            size={40}
            style={isRight ? styles.avatarRight : styles.avatarLeft}
            onPress={() => onAvatarPress?.(message)}
        />
    ) : null;

    /** 内容区（名称 + 气泡） - 头像隐藏时使用更大宽度 */
    const contentAreaStyle = [
        styles.contentArea,
        isRight && styles.contentAreaRight,
        !shouldShowAvatar && styles.contentAreaFullWidth,
    ];

    /** 内容区（名称 + 气泡） */
    const contentNode = (
        <View style={contentAreaStyle}>
            {/* 发送者名称 */}
            {showSenderName && senderName && (
                <Text style={[styles.senderName, isRight && styles.senderNameRight]}>
                    {senderName}
                </Text>
            )}

            {/* 气泡（支持长按） */}
            <TouchableOpacity
                activeOpacity={1}
                onLongPress={() => onLongPress?.(message)}
                delayLongPress={500}
            >
                <DbChatBubble
                    type={type}
                    direction={direction}
                    senderType={senderType}
                    content={content}
                    isStreaming={isStreaming}
                    isLoading={isLoading}
                    status={status}
                    showStatus={showStatus}
                    isRead={isRead}
                    onRetry={() => onRetry?.(id)}
                />
            </TouchableOpacity>
        </View>
    );

    return (
        <View style={[styles.messageRow, style]}>
            {/* 消息行：根据方向决定 头像/内容 的排列顺序 */}
            <View style={styles.contentRow}>
                {isRight ? (
                    <>
                        {contentNode}
                        {avatarNode}
                    </>
                ) : (
                    <>
                        {avatarNode}
                        {contentNode}
                    </>
                )}
            </View>

            {/* 时间 */}
            {showTime && (
                <DbChatMeta
                    timestamp={timestamp}
                    status={status}
                    direction={direction}
                    isRead={isRead}
                    showTime={showTime}
                    showStatus={showStatus}
                />
            )}
        </View>
    );
};

DbChatMessage.displayName = 'DbChatMessage';

export default DbChatMessage;
export type { DbChatMessageProps, ChatMessageData } from './types';
